// client/src/components/pages/RouteSummary/SummaryTable.tsx (converted from .js, ST-00216)
import {
  Fragment,
  useState,
  useRef,
  useEffect,
  useCallback,
  type MouseEvent,
  type ReactNode,
} from 'react';
import { FaFileExport, FaEye, FaCode, FaTimes, FaCalculator, FaInfoCircle } from 'react-icons/fa';
import api from '../../../services/api';
import { formatMiningDate } from '../../../utils/dateUtils';
import TransactionDetailsModal from './TransactionDetailsModal';
import SQLQueryModal from './SQLQueryModal';
import type {
  BfcfVariant,
  GenerateSqlResponse,
  GroupingId,
  MatchedVarianceInfo,
  RouteSummaryDayRow,
  RouteSummaryForwardRow,
  RouteSummaryGroupingInfo,
  RouteSummaryRequestError,
  SqlQueryMetadata,
  TransactionType,
} from '../../../types/clientPages/routeSummary';
import type { MatchingInfo, RouteFigures } from '../../../types/routeFigures';
import {
  fallbackNoteText,
  figureRows,
  matchedSubLabel as subLabelOf,
  notAvailableHint,
  summaryTotals,
  summaryVariancePct,
  varianceNotAvailable,
  type FigureRow,
} from './summaryFigures';

export interface SummaryTableProps {
  /** The per-day rows (`summary` of POST /route-summary/generate). */
  data: RouteSummaryDayRow[];
  /** Brought Forward row. */
  preload?: RouteSummaryForwardRow | null;
  carriedForward?: RouteSummaryForwardRow | null;
  /** The response's `grouping` plus the page's label as `name`. */
  grouping: RouteSummaryGroupingInfo;
  startDate: string;
  endDate: string;
  groupingId?: GroupingId | null;
  /** Every route the summary was generated with (ST-00215, RSO-6). */
  groupingIds?: GroupingId[] | null;
  minWeight?: number | null;
  matchedVariance?: MatchedVarianceInfo | null;
  /** Shown right under the Brought / Carried Forward rows: the load-matching preview (ST-00218). */
  bfcfPreview?: ReactNode;
  /** ST-00218 P3: how the figures were matched (the fallback note when set). */
  matching?: MatchingInfo | null;
  /** ST-00218 P3: the period figures (Truck + time routes: the adjusted bottom line + rows). */
  figures?: RouteFigures | null;
  /** ST-00218 P3: shown above the table — "N tickets probably on the wrong route — review". */
  locationNotice?: ReactNode;
}

/** One sum-calculator group: cell key -> numeric value. */
interface SelectionGroup {
  cells: Map<string, number>;
}

interface GroupColor {
  bg: string;
  text: string;
  hoverBg: string;
  dot: string;
  label: string;
}

// ST-00182 — shown in place of the per-day variance columns when a selected
// route has Load matching set to None (ST-00218 P3: the setting that replaced
// "BF/CF Ticket Matching"): without a ticket match a day's receipts cannot be
// tied to the dispatches they actually came from, and the old calendar
// arithmetic (receipts today minus dispatches today) was noise. Routes on
// Truck + time always have per-day variance (the load-match ledger pairs them).
const MATCHED_VARIANCE_NOTES = {
  none: "Per-day variance unavailable. This route has Load matching set to None, so a day's receipts can't be tied to the dispatches they came from. Use the period total below.",
  mixed:
    "Per-day variance unavailable. One or more of the selected routes has Load matching set to None, so receipts can't be tied to their dispatches. Use the period total below.",
};

// Per-day matched figures straight off the API row. `percent` is null when there
// is no matched dispatch weight to divide by (rendered as an em dash).
const matchedRowValues = (row: RouteSummaryDayRow) => {
  const matchedDispatch = Number(row.matchedDispatchWeight) || 0;
  const matchedReceipt = Number(row.matchedReceiptWeight) || 0;
  const weightVar = matchedReceipt - matchedDispatch;
  return {
    // A day with no matched loads has nothing to vary — render a dash, not "+0.00".
    hasMatches: (Number(row.matchedCount) || 0) > 0,
    weightVar,
    percent: matchedDispatch > 0 ? (weightVar / matchedDispatch) * 100 : null,
    unmatched: Number(row.unmatchedReceiptCount) || 0,
  };
};

// The note contains commas, so every CSV cell carrying it has to be quoted.
const csvQuote = (text: string) => `"${String(text).replace(/"/g, '""')}"`;

const fmt2 = (n: number) =>
  n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

interface FigureRowViewProps {
  row: FigureRow;
  matchedAvailable: boolean;
  onView: (() => void) | null;
}

// ST-00218 P3: one row after Carried Forward on a Truck + time route — a deduction (received
// off-route, no receipt data) shown like Carried Forward, or an information row (missing,
// needs review) whose loads stay in Dispatched.
const FigureRowView = ({ row, matchedAvailable, onView }: FigureRowViewProps) => {
  const sign = row.deducted ? '-' : '';
  const tone = row.deducted
    ? {
        tr: 'bg-amber-50/70 border-l-4 border-l-amber-400',
        num: 'text-amber-700',
        eye: 'text-amber-500 hover:text-amber-700',
      }
    : {
        tr: 'bg-slate-50 border-l-4 border-l-slate-400',
        num: 'text-slate-600',
        eye: 'text-slate-400 hover:text-slate-700',
      };
  const viewButton = onView ? (
    <button
      type="button"
      className={`${tone.eye} text-[10px] sm:text-xs flex-shrink-0 p-0.5`}
      onClick={onView}
      title={`View the loads: ${row.label}`}
      aria-label={`View the loads: ${row.label}`}
    >
      <FaEye />
    </button>
  ) : null;
  const dash = (extra = '') => (
    <td
      className={`px-3 sm:px-5 py-2.5 sm:py-3 whitespace-nowrap text-xs sm:text-sm text-right text-slate-300 border-t border-slate-200 ${extra}`}
    >
      —
    </td>
  );
  return (
    <tr className={`${tone.tr} font-semibold border-t border-t-slate-200`}>
      <td
        className="px-3 sm:px-5 py-2.5 sm:py-3 whitespace-nowrap text-xs sm:text-sm text-slate-800 border-t border-slate-200"
        title={row.explanation}
      >
        <div className="font-bold">{row.label}</div>
        <div className="text-[10px] sm:text-xs text-slate-500 font-normal">{row.sublabel}</div>
        <span className="sr-only">{row.explanation}</span>
      </td>
      {row.side === 'receipt' ? (
        <>
          {dash('border-l-2 border-l-orange-300/60')}
          {dash()}
        </>
      ) : (
        <>
          <td
            className={`px-3 sm:px-5 py-2.5 sm:py-3 whitespace-nowrap text-xs sm:text-sm text-right font-bold border-l-2 border-l-orange-300/60 border-t border-slate-200 ${tone.num}`}
          >
            <div className="flex items-center justify-end gap-1">
              <span>
                {sign}
                {fmt2(row.weight)}
              </span>
              {viewButton}
            </div>
          </td>
          <td
            className={`px-3 sm:px-5 py-2.5 sm:py-3 whitespace-nowrap text-xs sm:text-sm text-right font-bold border-t border-slate-200 ${tone.num}`}
          >
            <span>
              {sign}
              {row.count.toLocaleString()}
            </span>
          </td>
        </>
      )}
      {row.side === 'receipt' ? (
        <>
          <td
            className={`px-3 sm:px-5 py-2.5 sm:py-3 whitespace-nowrap text-xs sm:text-sm text-right font-bold border-l-2 border-l-emerald-300/60 border-t border-slate-200 ${tone.num}`}
          >
            <div className="flex items-center justify-end gap-1">
              <span>{fmt2(row.weight)}</span>
              {viewButton}
            </div>
          </td>
          <td
            className={`px-3 sm:px-5 py-2.5 sm:py-3 whitespace-nowrap text-xs sm:text-sm text-right font-bold border-t border-slate-200 ${tone.num}`}
          >
            <span>{row.count.toLocaleString()}</span>
          </td>
        </>
      ) : (
        <>
          {dash('border-l-2 border-l-emerald-300/60')}
          {dash()}
        </>
      )}
      {dash('border-l-2 border-l-slate-300/60')}
      {matchedAvailable && (
        <>
          {dash()}
          {dash()}
        </>
      )}
    </tr>
  );
};

const SummaryTable = ({
  data,
  preload,
  carriedForward,
  grouping,
  startDate,
  endDate,
  groupingId,
  groupingIds: groupingIdsProp,
  minWeight,
  matchedVariance,
  bfcfPreview,
  locationNotice,
  matching = null,
  figures = null,
}: SummaryTableProps) => {
  // Modal state
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedDate, setSelectedDate] = useState<string | null>(null);
  const [selectedType, setSelectedType] = useState<TransactionType | null>(null);
  // Set when the modal is showing the loads behind the Brought/Carried Forward row
  const [bfcfVariant, setBfcfVariant] = useState<BfcfVariant | null>(null);

  // Every route of the summary (ST-00215, RSO-6): the page passes the ids the
  // summary was generated with; combined responses also carry them, and a
  // single-route summary falls back to the one id.
  const groupingIds: GroupingId[] =
    Array.isArray(groupingIdsProp) && groupingIdsProp.length > 0
      ? groupingIdsProp
      : grouping.groupingIds && grouping.groupingIds.length > 0
        ? grouping.groupingIds
        : groupingId
          ? [groupingId]
          : [];

  // ST-00182 — per-day variance is the server's ticket-matched figure, anchored
  // on the receipt date. A response with no `matchedVariance` block (older
  // server, or a cache entry written before this shipped) counts as unavailable.
  const mv: MatchedVarianceInfo =
    matchedVariance && typeof matchedVariance === 'object'
      ? matchedVariance
      : { available: false, reason: 'none' };
  const matchedAvailable = mv.available === true;
  const matchedModes = Array.isArray(mv.modes) ? mv.modes.filter(Boolean) : [];
  // Indexed like the JS original: a null/unknown reason falls back to `none`.
  const matchedUnavailableNote =
    (MATCHED_VARIANCE_NOTES as Record<string, string | undefined>)[String(mv.reason)] ||
    MATCHED_VARIANCE_NOTES.none;
  // Legacy modes keep their label; Truck + time reads "matched on receipt date · truck + time".
  const matchedSubLabel = subLabelOf({ ...mv, modes: matchedModes });
  // Three columns when matched per-day numbers exist, one note column when not.
  const varianceColSpan = matchedAvailable ? 3 : 1;

  // SQL Query modal state
  const [sqlModalOpen, setSqlModalOpen] = useState(false);
  const [sqlQuery, setSqlQuery] = useState('');
  const [sqlMetadata, setSqlMetadata] = useState<SqlQueryMetadata | null>(null);
  const [sqlLoading, setSqlLoading] = useState(false);
  const [sqlError, setSqlError] = useState<string | null>(null);

  // Multi-group weight selection for sum calculator
  // Each group: { id, cells: Map<cellKey, numericValue>, color }
  const GROUP_COLORS: GroupColor[] = [
    {
      bg: 'bg-blue-100',
      text: 'text-blue-900',
      hoverBg: 'hover:bg-blue-200',
      dot: 'bg-blue-400',
      label: 'A',
    },
    {
      bg: 'bg-amber-100',
      text: 'text-amber-900',
      hoverBg: 'hover:bg-amber-200',
      dot: 'bg-amber-400',
      label: 'B',
    },
    {
      bg: 'bg-violet-100',
      text: 'text-violet-900',
      hoverBg: 'hover:bg-violet-200',
      dot: 'bg-violet-400',
      label: 'C',
    },
  ];
  const [selectionGroups, setSelectionGroups] = useState<SelectionGroup[]>([]); // array of { cells: Map<key, value> }

  // Drag state
  const isDragging = useRef(false);
  const activeGroupIndex = useRef(-1);
  const [isDraggingState, setIsDraggingState] = useState(false);

  // Find which group a cell belongs to (returns index or -1)
  const getCellGroupIndex = useCallback(
    (key: string) => {
      for (let i = 0; i < selectionGroups.length; i++) {
        if (selectionGroups[i]!.cells.has(key)) return i;
      }
      return -1;
    },
    [selectionGroups]
  );

  // Start drag on mousedown
  const handleCellMouseDown = useCallback(
    (e: MouseEvent<HTMLTableCellElement>, key: string, value: number) => {
      e.preventDefault();
      isDragging.current = true;
      setIsDraggingState(true);

      const existingGroup = getCellGroupIndex(key);

      if (existingGroup >= 0) {
        // Clicking on a cell already in a group: remove entire group
        setSelectionGroups((prev) => prev.filter((_, i) => i !== existingGroup));
        activeGroupIndex.current = -1; // no active painting
        isDragging.current = false;
        setIsDraggingState(false);
      } else {
        // Start a new group (max 3)
        setSelectionGroups((prev) => {
          const groups = prev.length >= GROUP_COLORS.length ? prev.slice(1) : [...prev];
          const newGroup = { cells: new Map([[key, value]]) };
          activeGroupIndex.current = groups.length;
          return [...groups, newGroup];
        });
      }
    },
    [getCellGroupIndex]
  );

  // Continue drag on mouseenter
  const handleCellMouseEnter = useCallback(
    (key: string, value: number) => {
      if (!isDragging.current || activeGroupIndex.current < 0) return;

      // Only add if cell isn't already in another group
      const existingGroup = getCellGroupIndex(key);
      if (existingGroup >= 0 && existingGroup !== activeGroupIndex.current) return;

      setSelectionGroups((prev) => {
        const idx = activeGroupIndex.current;
        if (idx >= prev.length) return prev;
        const updated = [...prev];
        const newCells = new Map(updated[idx]!.cells);
        newCells.set(key, value);
        updated[idx] = { cells: newCells };
        return updated;
      });
    },
    [getCellGroupIndex]
  );

  // Stop drag on mouseup
  useEffect(() => {
    const handleMouseUp = () => {
      if (isDragging.current) {
        isDragging.current = false;
        activeGroupIndex.current = -1;
        setIsDraggingState(false);
      }
    };
    document.addEventListener('mouseup', handleMouseUp);
    return () => document.removeEventListener('mouseup', handleMouseUp);
  }, []);

  // Clear a single group
  const clearGroup = (groupIndex: number) => {
    setSelectionGroups((prev) => prev.filter((_, i) => i !== groupIndex));
  };

  // Clear all groups
  const clearAllGroups = () => {
    setSelectionGroups([]);
  };

  // Get group sum
  const getGroupSum = (group: SelectionGroup) => {
    let sum = 0;
    group.cells.forEach((val) => {
      sum += val;
    });
    return sum;
  };

  // Get cell styling based on group membership
  const getCellGroupStyle = (key: string): GroupColor | null | undefined => {
    for (let i = 0; i < selectionGroups.length; i++) {
      if (selectionGroups[i]!.cells.has(key)) return GROUP_COLORS[i];
    }
    return null;
  };

  const hasAnySelection = selectionGroups.length > 0;

  // Calculate totals including brought forward (BF) and carried forward (CF)
  // BF adds to dispatch side (loads dispatched before period, receipted within)
  // CF deducts from dispatch side (loads dispatched within period, not yet receipted)
  // ST-00218 P3: on a Truck + time route the bottom line is the server's adjusted dispatched
  // (also less received off-route and no receipt data); otherwise the arithmetic of before.
  const totals = summaryTotals(data, preload, carriedForward, figures);
  const engineRows = figureRows(figures);
  // Weight variance percent from the bottom line; ST-00298: null = n/a, receipts against
  // nothing left dispatched (an adjusted dispatch of 0.5 t or less).
  const variancePct = summaryVariancePct(totals);
  const varianceNa = varianceNotAvailable(figures) || variancePct === null;
  const naHint = notAvailableHint(figures, grouping, totals);
  const fallbackNote = fallbackNoteText(matching);
  const showFallbackNote = !!(matching && matching.fallback && matching.note);

  // Calculate variance totals
  const countVariance = totals.receiptCount - totals.dispatchCount;
  const weightVariance = totals.receiptWeight - totals.dispatchWeight;
  // Read only where the variance is not n/a, so always the bottom line's number there.
  const weightVariancePercent = variancePct ?? 0;

  // Export to CSV
  //
  // ST-00182 — the trailing columns follow the table: three matched-variance
  // columns when per-day matching is available, otherwise a single note column
  // plus three explicit TOTAL columns, so a note can never land in a cell Excel
  // would read as a number.
  const exportToCSV = () => {
    const headers = [
      'Date',
      'Dispatch Weight (tonnes)',
      'Dispatch Count',
      'Receipt Weight (tonnes)',
      'Receipt Count',
      ...(matchedAvailable
        ? ['Matched Wt Var (t)', 'Matched Var %', 'Unmatched']
        : [
            'Matched Variance',
            'Total Count Variance',
            'Total Weight Variance (tonnes)',
            'Total Weight Variance %',
          ]),
    ];
    // Blank trailing cells for the Brought/Carried Forward rows.
    const blankVarianceCells = matchedAvailable ? ['', '', ''] : ['', '', '', ''];
    const csvData = [
      headers.join(','),
      // Add brought forward row if it has data
      ...(preload && preload.dispatchCount > 0
        ? [
            [
              'Brought Forward',
              `+${preload.dispatchWeight.toFixed(2)}`,
              `+${preload.dispatchCount}`,
              '',
              '',
              ...blankVarianceCells,
            ].join(','),
          ]
        : []),
      ...data.map((row) => {
        const rowMatched = matchedRowValues(row);

        return [
          row.date,
          row.dispatchWeight.toFixed(2),
          row.dispatchCount,
          row.receiptWeight.toFixed(2),
          row.receiptCount,
          ...(matchedAvailable
            ? [
                rowMatched.hasMatches ? rowMatched.weightVar.toFixed(2) : '',
                rowMatched.percent === null ? '' : rowMatched.percent.toFixed(2),
                rowMatched.unmatched,
              ]
            : [csvQuote(matchedUnavailableNote), '', '', '']),
        ].join(',');
      }),
      // Add carried forward row if it has data
      ...(carriedForward && carriedForward.dispatchCount > 0
        ? [
            [
              'Carried Forward',
              `-${carriedForward.dispatchWeight.toFixed(2)}`,
              `-${carriedForward.dispatchCount}`,
              '',
              '',
              ...blankVarianceCells,
            ].join(','),
          ]
        : []),
      // ST-00218 P3 (Truck + time): received off-route / no receipt data are deducted like
      // CF; missing / needs-review loads are listed and stay in Dispatched.
      ...engineRows.map((r) =>
        [
          r.deducted ? r.label : `${r.label} (included in Dispatched)`,
          `${r.deducted ? '-' : ''}${r.weight.toFixed(2)}`,
          `${r.deducted ? '-' : ''}${r.count}`,
          '',
          '',
          ...blankVarianceCells,
        ].join(',')
      ),
      // Add totals row — the period bottom line (BF/CF-adjusted; n/a without receipt data).
      [
        'TOTAL',
        totals.dispatchWeight.toFixed(2),
        totals.dispatchCount,
        totals.receiptWeight.toFixed(2),
        totals.receiptCount,
        ...(matchedAvailable
          ? [
              varianceNa ? 'n/a' : weightVariance.toFixed(2),
              varianceNa ? 'n/a' : weightVariancePercent.toFixed(2),
              countVariance,
            ]
          : [
              '',
              countVariance,
              varianceNa ? 'n/a' : weightVariance.toFixed(2),
              varianceNa ? 'n/a' : weightVariancePercent.toFixed(2),
            ]),
      ].join(','),
      ...(showFallbackNote && fallbackNote ? [`Note,${csvQuote(fallbackNote)}`] : []),
    ].join('\n');

    const blob = new Blob([csvData], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `route-summary-${startDate}-to-${endDate}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  };

  // Handle cell click
  const handleCellClick = (date: string, type: TransactionType) => {
    setBfcfVariant(null);
    setSelectedDate(date);
    setSelectedType(type);
    setModalOpen(true);
  };

  // Open the loads behind a Brought Forward / Carried Forward row
  const handleBfcfClick = (variant: BfcfVariant) => {
    setSelectedDate(null);
    setSelectedType(null);
    setBfcfVariant(variant);
    setModalOpen(true);
  };

  // Close modal
  const handleModalClose = () => {
    setModalOpen(false);
    setSelectedDate(null);
    setSelectedType(null);
    setBfcfVariant(null);
  };

  // Generate SQL Query
  const handleGenerateSQLQuery = async () => {
    setSqlLoading(true);
    setSqlError(null);
    setSqlModalOpen(true);

    try {
      const response = await api.routeSummary.generateSQLQuery({
        groupingId: groupingIds[0] || groupingId,
        // A combined summary reproduces all of its routes, not the first one.
        groupingIds,
        startDate,
        endDate,
        minWeight,
      });

      const sql = response.data as GenerateSqlResponse;
      setSqlQuery(sql.query);
      setSqlMetadata(sql.metadata);
    } catch (caught) {
      const err = caught as RouteSummaryRequestError;
      console.error('Error generating SQL query:', caught);
      setSqlError(err.response?.data?.error || 'Failed to generate SQL query');
    } finally {
      setSqlLoading(false);
    }
  };

  // Close SQL modal
  const handleSqlModalClose = () => {
    setSqlModalOpen(false);
    setSqlQuery('');
    setSqlMetadata(null);
    setSqlError(null);
  };

  return (
    <div className="bg-surface border border-slate-200 rounded-xl shadow-sm overflow-hidden">
      {/* Header - Responsive design */}
      <div className="px-4 sm:px-6 py-3 sm:py-4 bg-surface border-b border-slate-200">
        <div className="flex items-center justify-between gap-3">
          <h2 className="text-sm sm:text-base font-bold text-slate-900">Summary Results</h2>
          <div className="flex items-center gap-1.5 sm:gap-2">
            <button
              onClick={handleGenerateSQLQuery}
              className="flex items-center gap-1.5 sm:gap-2 px-2.5 sm:px-4 py-1.5 sm:py-2 bg-slate-100 text-slate-700 rounded-lg hover:bg-slate-200 transition-all duration-200 text-xs sm:text-sm font-medium"
              title="Generate SQL query for this route summary"
            >
              <FaCode className="text-xs sm:text-sm" />
              <span className="hidden sm:inline">SQL Query</span>
              <span className="sm:hidden">SQL</span>
            </button>
            <button
              onClick={exportToCSV}
              className="flex items-center gap-1.5 sm:gap-2 px-2.5 sm:px-4 py-1.5 sm:py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 transition-all duration-200 text-xs sm:text-sm font-medium"
            >
              <FaFileExport className="text-xs sm:text-sm" />
              <span className="hidden sm:inline">Export CSV</span>
              <span className="sm:hidden">CSV</span>
            </button>
          </div>
        </div>
      </div>

      {/* Grouping Info - Responsive design */}
      <div className="px-4 sm:px-6 py-2.5 sm:py-3 bg-slate-50 border-b border-slate-200">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 sm:gap-3 text-xs">
          <div className="flex items-start gap-2.5">
            <div className="w-2 h-2 bg-orange-500 rounded-full flex-shrink-0 mt-1"></div>
            <div className="min-w-0">
              <span className="font-semibold text-slate-700">Dispatch: </span>
              <span className="text-slate-900 break-words">
                {grouping.dispatchLocations.join(', ')}
              </span>
            </div>
          </div>
          <div className="flex items-start gap-2.5">
            <div className="w-2 h-2 bg-emerald-500 rounded-full flex-shrink-0 mt-1"></div>
            <div className="min-w-0">
              <span className="font-semibold text-slate-700">Receipt: </span>
              <span className="text-slate-900 break-words">
                {grouping.receiptLocations.join(', ')}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* ST-00218 P3: a Truck + time route that shows delivery-number figures says so */}
      {showFallbackNote && (
        <div
          role="note"
          title={matching?.detail || undefined}
          className="px-4 sm:px-6 py-2 border-b border-amber-200 bg-amber-50 text-[11px] sm:text-xs text-amber-900 flex items-start gap-2"
        >
          <FaInfoCircle className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" aria-hidden="true" />
          <p className="min-w-0 break-words">
            <span className="font-semibold">{matching?.note}</span>
            {matching?.detail && <span className="text-amber-800"> — {matching.detail}</span>}
          </p>
        </div>
      )}

      {locationNotice}

      {/* Table - Modern, clean design with better hierarchy */}
      <div className={`overflow-x-auto ${isDraggingState ? 'select-none' : ''}`}>
        <table className="w-full border-collapse">
          <thead className="sticky top-0 z-10">
            {/* Section group header */}
            <tr className="bg-slate-800 dark:bg-slate-50">
              <th
                className="px-3 sm:px-5 py-1.5 sm:py-2 bg-slate-900 dark:bg-slate-50 border-b border-slate-700 dark:border-slate-200"
                rowSpan={1}
              ></th>
              <th
                className="px-3 sm:px-5 py-1.5 sm:py-2 text-center text-[9px] sm:text-[10px] font-bold uppercase tracking-widest text-orange-300 dark:text-orange-800 bg-orange-950/60 dark:bg-orange-50/60 border-l-2 border-l-orange-400/50 border-b border-orange-800/40 dark:border-orange-300/40"
                colSpan={2}
              >
                Dispatch
              </th>
              <th
                className="px-3 sm:px-5 py-1.5 sm:py-2 text-center text-[9px] sm:text-[10px] font-bold uppercase tracking-widest text-emerald-300 dark:text-emerald-800 bg-emerald-950/60 dark:bg-emerald-50/60 border-l-2 border-l-emerald-400/50 border-b border-emerald-800/40 dark:border-emerald-300/40"
                colSpan={2}
              >
                Receipt
              </th>
              <th
                className="px-3 sm:px-5 py-1.5 sm:py-2 text-center text-[9px] sm:text-[10px] font-bold uppercase tracking-widest text-slate-400 dark:text-slate-600 bg-slate-800 dark:bg-slate-50 border-l-2 border-l-slate-500/50 dark:border-l-slate-400/50 border-b border-slate-700 dark:border-slate-200"
                colSpan={varianceColSpan}
              >
                <div>Matched Variance</div>
                <div className="text-[8px] sm:text-[9px] font-medium normal-case tracking-normal text-slate-500">
                  {matchedSubLabel}
                </div>
              </th>
            </tr>
            {/* Column headers */}
            <tr className="bg-slate-900 dark:bg-slate-50 text-white">
              <th className="px-3 sm:px-5 py-2 sm:py-3 text-left text-[10px] sm:text-xs font-bold uppercase tracking-wider whitespace-nowrap border-b-2 border-slate-600 dark:border-slate-300">
                Date
              </th>
              <th className="px-3 sm:px-5 py-2 sm:py-3 text-right text-[10px] sm:text-xs font-bold uppercase tracking-wider bg-orange-900/40 dark:bg-orange-200/40 border-l-2 border-l-orange-400/50 border-b-2 border-orange-700/50 dark:border-orange-300/50 whitespace-nowrap">
                <span className="hidden md:inline">Weight (t)</span>
                <span className="md:hidden">Wt</span>
              </th>
              <th className="px-3 sm:px-5 py-2 sm:py-3 text-right text-[10px] sm:text-xs font-bold uppercase tracking-wider bg-orange-900/40 dark:bg-orange-200/40 border-b-2 border-orange-700/50 dark:border-orange-300/50 whitespace-nowrap">
                <span className="hidden md:inline">Count</span>
                <span className="md:hidden">#</span>
              </th>
              <th className="px-3 sm:px-5 py-2 sm:py-3 text-right text-[10px] sm:text-xs font-bold uppercase tracking-wider bg-emerald-900/40 dark:bg-emerald-200/40 border-l-2 border-l-emerald-400/50 border-b-2 border-emerald-700/50 dark:border-emerald-300/50 whitespace-nowrap">
                <span className="hidden md:inline">Weight (t)</span>
                <span className="md:hidden">Wt</span>
              </th>
              <th className="px-3 sm:px-5 py-2 sm:py-3 text-right text-[10px] sm:text-xs font-bold uppercase tracking-wider bg-emerald-900/40 dark:bg-emerald-200/40 border-b-2 border-emerald-700/50 dark:border-emerald-300/50 whitespace-nowrap">
                <span className="hidden md:inline">Count</span>
                <span className="md:hidden">#</span>
              </th>
              {matchedAvailable ? (
                <>
                  <th className="px-3 sm:px-5 py-2 sm:py-3 text-right text-[10px] sm:text-xs font-bold uppercase tracking-wider bg-slate-800 dark:bg-slate-50 border-l-2 border-l-slate-500/50 dark:border-l-slate-400/50 border-b-2 border-slate-600 dark:border-slate-300 whitespace-nowrap">
                    <span className="hidden md:inline">Matched Wt Var (t)</span>
                    <span className="md:hidden">Wt Var</span>
                  </th>
                  <th className="px-3 sm:px-5 py-2 sm:py-3 text-right text-[10px] sm:text-xs font-bold uppercase tracking-wider bg-slate-800 dark:bg-slate-50 border-b-2 border-slate-600 dark:border-slate-300 whitespace-nowrap">
                    <span className="hidden md:inline">Matched Var %</span>
                    <span className="md:hidden">Var%</span>
                  </th>
                  <th className="px-3 sm:px-5 py-2 sm:py-3 text-right text-[10px] sm:text-xs font-bold uppercase tracking-wider bg-slate-800 dark:bg-slate-50 border-b-2 border-slate-600 dark:border-slate-300 whitespace-nowrap">
                    <span className="hidden md:inline">Unmatched</span>
                    <span className="md:hidden">Unm</span>
                  </th>
                </>
              ) : (
                <th className="px-3 sm:px-5 py-2 sm:py-3 text-right text-[10px] sm:text-xs font-bold uppercase tracking-wider bg-slate-800 dark:bg-slate-50 border-l-2 border-l-slate-500/50 dark:border-l-slate-400/50 border-b-2 border-slate-600 dark:border-slate-300 whitespace-nowrap">
                  <span className="hidden md:inline">Weight (t) / % / Count</span>
                  <span className="md:hidden">Var</span>
                </th>
              )}
            </tr>
          </thead>
          <tbody className="bg-surface">
            {/* Brought Forward row - only show if there are actual cross-day matches */}
            {preload && preload.dispatchCount > 0 && (
              <tr className="bg-blue-50 font-semibold border-l-4 border-l-blue-500 border-b border-b-slate-200">
                <td className="px-3 sm:px-5 py-3 sm:py-4 whitespace-nowrap text-xs sm:text-sm text-slate-800 border-b border-blue-200">
                  <div className="font-bold">Brought Forward</div>
                  <div className="text-[10px] sm:text-xs text-slate-500">
                    Pre-loads receipted in period
                  </div>
                </td>
                <td className="px-3 sm:px-5 py-3 sm:py-4 whitespace-nowrap text-xs sm:text-sm text-right text-blue-700 font-bold border-l-2 border-l-orange-300/60 border-b border-blue-200 bg-orange-50/30">
                  <div className="flex items-center justify-end gap-1">
                    <span>
                      +
                      {preload.dispatchWeight.toLocaleString(undefined, {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })}
                    </span>
                    {groupingIds.length > 0 && (
                      <button
                        className="text-blue-400 hover:text-blue-600 text-[10px] sm:text-xs flex-shrink-0 p-0.5"
                        onClick={() => handleBfcfClick('brought-forward')}
                        title="View the loads making up Brought Forward"
                      >
                        <FaEye />
                      </button>
                    )}
                  </div>
                </td>
                <td className="px-3 sm:px-5 py-3 sm:py-4 whitespace-nowrap text-xs sm:text-sm text-right text-blue-700 font-bold border-b border-blue-200 bg-orange-50/30">
                  <div className="flex items-center justify-end gap-1">
                    <span>+{preload.dispatchCount.toLocaleString()}</span>
                    {groupingIds.length > 0 && (
                      <button
                        className="text-blue-400 hover:text-blue-600 text-[10px] sm:text-xs flex-shrink-0 p-0.5"
                        onClick={() => handleBfcfClick('brought-forward')}
                        title="View the loads making up Brought Forward"
                      >
                        <FaEye />
                      </button>
                    )}
                  </div>
                </td>
                <td className="px-3 sm:px-5 py-3 sm:py-4 whitespace-nowrap text-xs sm:text-sm text-right text-slate-300 border-l-2 border-l-emerald-300/60 border-b border-blue-200 bg-emerald-50/20">
                  —
                </td>
                <td className="px-3 sm:px-5 py-3 sm:py-4 whitespace-nowrap text-xs sm:text-sm text-right text-slate-300 border-b border-blue-200 bg-emerald-50/20">
                  —
                </td>
                <td className="px-3 sm:px-5 py-3 sm:py-4 whitespace-nowrap text-xs sm:text-sm text-right text-slate-300 border-l-2 border-l-slate-300/60 border-b border-blue-200">
                  —
                </td>
                {matchedAvailable && (
                  <>
                    <td className="px-3 sm:px-5 py-3 sm:py-4 whitespace-nowrap text-xs sm:text-sm text-right text-slate-300 border-b border-blue-200">
                      —
                    </td>
                    <td className="px-3 sm:px-5 py-3 sm:py-4 whitespace-nowrap text-xs sm:text-sm text-right text-slate-300 border-b border-blue-200">
                      —
                    </td>
                  </>
                )}
              </tr>
            )}
            {data.map((row, index) => {
              // ST-00182 — matched (ticket-to-ticket) variance from the server.
              // The old calendar arithmetic that used to live here compared
              // today's receipts with today's dispatches, which is meaningless
              // once a load takes more than a day to arrive.
              const rowMatched = matchedRowValues(row);

              const dwKey = `row-${index}-dispatchWeight`;
              const dcKey = `row-${index}-dispatchCount`;
              const rwKey = `row-${index}-receiptWeight`;
              const rcKey = `row-${index}-receiptCount`;

              const stripeBg = index % 2 === 0 ? 'bg-surface' : 'bg-slate-50/80';

              return (
                <tr
                  key={index}
                  className={`${stripeBg} hover:bg-slate-100/80 transition-colors duration-100 border-b border-slate-100`}
                >
                  <td className="px-3 sm:px-5 py-2.5 sm:py-3 whitespace-nowrap text-xs sm:text-sm font-semibold text-slate-900">
                    {formatMiningDate(row.date)}
                  </td>
                  {/* Dispatch Weight */}
                  {(() => {
                    const gs = getCellGroupStyle(dwKey);
                    return (
                      <td
                        className={`px-3 sm:px-5 py-2.5 sm:py-3 whitespace-nowrap text-xs sm:text-sm text-right font-semibold transition-colors duration-100 cursor-cell border-l-2 border-l-orange-300/60 ${
                          gs
                            ? `${gs.bg} ${gs.text}`
                            : 'text-orange-700 bg-orange-50/20 hover:bg-orange-50/60'
                        }`}
                        onMouseDown={(e) => handleCellMouseDown(e, dwKey, row.dispatchWeight)}
                        onMouseEnter={() => handleCellMouseEnter(dwKey, row.dispatchWeight)}
                      >
                        <div className="flex items-center justify-end gap-1">
                          <span>
                            {row.dispatchWeight.toLocaleString(undefined, {
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2,
                            })}
                          </span>
                          {row.dispatchCount > 0 && (
                            <button
                              className="text-orange-400 hover:text-orange-600 text-[10px] sm:text-xs flex-shrink-0 p-0.5"
                              onMouseDown={(e) => e.stopPropagation()}
                              onClick={() => handleCellClick(row.date, 'dispatch')}
                              title="View dispatch details"
                            >
                              <FaEye />
                            </button>
                          )}
                        </div>
                      </td>
                    );
                  })()}
                  {/* Dispatch Count */}
                  {(() => {
                    const gs = getCellGroupStyle(dcKey);
                    return (
                      <td
                        className={`px-3 sm:px-5 py-2.5 sm:py-3 whitespace-nowrap text-xs sm:text-sm text-right font-semibold transition-colors duration-100 cursor-cell ${
                          gs
                            ? `${gs.bg} ${gs.text}`
                            : 'text-orange-700 bg-orange-50/20 hover:bg-orange-50/60'
                        }`}
                        onMouseDown={(e) => handleCellMouseDown(e, dcKey, row.dispatchCount)}
                        onMouseEnter={() => handleCellMouseEnter(dcKey, row.dispatchCount)}
                      >
                        <div className="flex items-center justify-end gap-1">
                          <span>{row.dispatchCount.toLocaleString()}</span>
                          {row.dispatchCount > 0 && (
                            <button
                              className="text-orange-400 hover:text-orange-600 text-[10px] sm:text-xs flex-shrink-0 p-0.5"
                              onMouseDown={(e) => e.stopPropagation()}
                              onClick={() => handleCellClick(row.date, 'dispatch')}
                              title="View dispatch details"
                            >
                              <FaEye />
                            </button>
                          )}
                        </div>
                      </td>
                    );
                  })()}
                  {/* Receipt Weight */}
                  {(() => {
                    const gs = getCellGroupStyle(rwKey);
                    return (
                      <td
                        className={`px-3 sm:px-5 py-2.5 sm:py-3 whitespace-nowrap text-xs sm:text-sm text-right font-semibold transition-colors duration-100 cursor-cell border-l-2 border-l-emerald-300/60 ${
                          gs
                            ? `${gs.bg} ${gs.text}`
                            : 'text-emerald-700 bg-emerald-50/20 hover:bg-emerald-50/60'
                        }`}
                        onMouseDown={(e) => handleCellMouseDown(e, rwKey, row.receiptWeight)}
                        onMouseEnter={() => handleCellMouseEnter(rwKey, row.receiptWeight)}
                      >
                        <div className="flex items-center justify-end gap-1">
                          <span>
                            {row.receiptWeight.toLocaleString(undefined, {
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2,
                            })}
                          </span>
                          {row.receiptCount > 0 && (
                            <button
                              className="text-emerald-400 hover:text-emerald-600 text-[10px] sm:text-xs flex-shrink-0 p-0.5"
                              onMouseDown={(e) => e.stopPropagation()}
                              onClick={() => handleCellClick(row.date, 'receipt')}
                              title="View receipt details"
                            >
                              <FaEye />
                            </button>
                          )}
                        </div>
                      </td>
                    );
                  })()}
                  {/* Receipt Count */}
                  {(() => {
                    const gs = getCellGroupStyle(rcKey);
                    return (
                      <td
                        className={`px-3 sm:px-5 py-2.5 sm:py-3 whitespace-nowrap text-xs sm:text-sm text-right font-semibold transition-colors duration-100 cursor-cell ${
                          gs
                            ? `${gs.bg} ${gs.text}`
                            : 'text-emerald-700 bg-emerald-50/20 hover:bg-emerald-50/60'
                        }`}
                        onMouseDown={(e) => handleCellMouseDown(e, rcKey, row.receiptCount)}
                        onMouseEnter={() => handleCellMouseEnter(rcKey, row.receiptCount)}
                      >
                        <div className="flex items-center justify-end gap-1">
                          <span>{row.receiptCount.toLocaleString()}</span>
                          {row.receiptCount > 0 && (
                            <button
                              className="text-emerald-400 hover:text-emerald-600 text-[10px] sm:text-xs flex-shrink-0 p-0.5"
                              onMouseDown={(e) => e.stopPropagation()}
                              onClick={() => handleCellClick(row.date, 'receipt')}
                              title="View receipt details"
                            >
                              <FaEye />
                            </button>
                          )}
                        </div>
                      </td>
                    );
                  })()}
                  {matchedAvailable ? (
                    <>
                      <td
                        className={`px-3 sm:px-5 py-2.5 sm:py-3 whitespace-nowrap text-xs sm:text-sm text-right font-bold border-l-2 border-l-slate-300/60 ${
                          !rowMatched.hasMatches
                            ? 'text-slate-400'
                            : rowMatched.weightVar >= 0
                              ? 'text-emerald-700'
                              : 'text-red-600'
                        }`}
                      >
                        {!rowMatched.hasMatches ? (
                          '—'
                        ) : (
                          <>
                            {rowMatched.weightVar >= 0 ? '+' : ''}
                            {rowMatched.weightVar.toLocaleString(undefined, {
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2,
                            })}
                          </>
                        )}
                      </td>
                      <td
                        className={`px-3 sm:px-5 py-2.5 sm:py-3 whitespace-nowrap text-xs sm:text-sm text-right font-bold ${
                          rowMatched.percent === null
                            ? 'text-slate-400'
                            : rowMatched.percent >= 0
                              ? 'text-emerald-700'
                              : 'text-red-600'
                        }`}
                      >
                        {rowMatched.percent === null ? (
                          '—'
                        ) : (
                          <>
                            {rowMatched.percent >= 0 ? '+' : ''}
                            {rowMatched.percent.toFixed(2)}%
                          </>
                        )}
                      </td>
                      <td
                        className={`px-3 sm:px-5 py-2.5 sm:py-3 whitespace-nowrap text-xs sm:text-sm text-right font-semibold ${rowMatched.unmatched > 0 ? 'text-amber-600' : 'text-slate-400'}`}
                        title={
                          rowMatched.unmatched > 0
                            ? 'Receipts on this day with no matching dispatch ticket'
                            : undefined
                        }
                      >
                        {rowMatched.unmatched.toLocaleString()}
                      </td>
                    </>
                  ) : (
                    index === 0 && (
                      <td
                        rowSpan={data.length}
                        className="px-3 sm:px-5 py-2.5 sm:py-3 align-middle text-[11px] sm:text-xs leading-relaxed text-slate-500 border-l-2 border-l-slate-300/60 max-w-[16rem] whitespace-normal"
                      >
                        {matchedUnavailableNote}
                      </td>
                    )
                  )}
                </tr>
              );
            })}
            {/* Carried Forward row - only show if there are unmatched dispatches */}
            {carriedForward && carriedForward.dispatchCount > 0 && (
              <tr className="bg-amber-50 font-semibold border-l-4 border-l-amber-500 border-t border-t-slate-200 border-b border-b-slate-200">
                <td className="px-3 sm:px-5 py-3 sm:py-4 whitespace-nowrap text-xs sm:text-sm text-slate-800 border-t border-amber-200">
                  <div className="font-bold">Carried Forward</div>
                  <div className="text-[10px] sm:text-xs text-slate-500">
                    Dispatches not yet receipted
                  </div>
                </td>
                <td className="px-3 sm:px-5 py-3 sm:py-4 whitespace-nowrap text-xs sm:text-sm text-right text-amber-700 font-bold border-l-2 border-l-orange-300/60 border-t border-amber-200 bg-orange-50/30">
                  <div className="flex items-center justify-end gap-1">
                    <span>
                      -
                      {carriedForward.dispatchWeight.toLocaleString(undefined, {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })}
                    </span>
                    {groupingIds.length > 0 && (
                      <button
                        className="text-amber-500 hover:text-amber-700 text-[10px] sm:text-xs flex-shrink-0 p-0.5"
                        onClick={() => handleBfcfClick('carried-forward')}
                        title="View the loads making up Carried Forward"
                      >
                        <FaEye />
                      </button>
                    )}
                  </div>
                </td>
                <td className="px-3 sm:px-5 py-3 sm:py-4 whitespace-nowrap text-xs sm:text-sm text-right text-amber-700 font-bold border-t border-amber-200 bg-orange-50/30">
                  <div className="flex items-center justify-end gap-1">
                    <span>-{carriedForward.dispatchCount.toLocaleString()}</span>
                    {groupingIds.length > 0 && (
                      <button
                        className="text-amber-500 hover:text-amber-700 text-[10px] sm:text-xs flex-shrink-0 p-0.5"
                        onClick={() => handleBfcfClick('carried-forward')}
                        title="View the loads making up Carried Forward"
                      >
                        <FaEye />
                      </button>
                    )}
                  </div>
                </td>
                <td className="px-3 sm:px-5 py-3 sm:py-4 whitespace-nowrap text-xs sm:text-sm text-right text-slate-300 border-l-2 border-l-emerald-300/60 border-t border-amber-200 bg-emerald-50/20">
                  —
                </td>
                <td className="px-3 sm:px-5 py-3 sm:py-4 whitespace-nowrap text-xs sm:text-sm text-right text-slate-300 border-t border-amber-200 bg-emerald-50/20">
                  —
                </td>
                <td className="px-3 sm:px-5 py-3 sm:py-4 whitespace-nowrap text-xs sm:text-sm text-right text-slate-300 border-l-2 border-l-slate-300/60 border-t border-amber-200">
                  —
                </td>
                {matchedAvailable && (
                  <>
                    <td className="px-3 sm:px-5 py-3 sm:py-4 whitespace-nowrap text-xs sm:text-sm text-right text-slate-300 border-t border-amber-200">
                      —
                    </td>
                    <td className="px-3 sm:px-5 py-3 sm:py-4 whitespace-nowrap text-xs sm:text-sm text-right text-slate-300 border-t border-amber-200">
                      —
                    </td>
                  </>
                )}
              </tr>
            )}
            {engineRows.map((r) => (
              <FigureRowView
                key={r.variant}
                row={r}
                matchedAvailable={matchedAvailable}
                onView={groupingIds.length > 0 ? () => handleBfcfClick(r.variant) : null}
              />
            ))}
          </tbody>
          <tfoot className="bg-slate-900 dark:bg-slate-50 text-white font-bold sticky bottom-0 z-10">
            <tr className="border-t-2 border-slate-500 dark:border-slate-400">
              <td className="px-3 sm:px-5 py-3.5 sm:py-4 whitespace-nowrap text-xs sm:text-sm font-bold uppercase tracking-wide">
                Total
              </td>
              <td className="px-3 sm:px-5 py-3.5 sm:py-4 whitespace-nowrap text-sm sm:text-base text-right font-bold border-l-2 border-l-orange-400/40 bg-orange-900/20 dark:bg-orange-200/20">
                {totals.dispatchWeight.toLocaleString(undefined, {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })}
              </td>
              <td className="px-3 sm:px-5 py-3.5 sm:py-4 whitespace-nowrap text-sm sm:text-base text-right font-bold bg-orange-900/20 dark:bg-orange-200/20">
                {totals.dispatchCount.toLocaleString()}
              </td>
              <td className="px-3 sm:px-5 py-3.5 sm:py-4 whitespace-nowrap text-sm sm:text-base text-right font-bold border-l-2 border-l-emerald-400/40 bg-emerald-900/20 dark:bg-emerald-200/20">
                {totals.receiptWeight.toLocaleString(undefined, {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })}
              </td>
              <td className="px-3 sm:px-5 py-3.5 sm:py-4 whitespace-nowrap text-sm sm:text-base text-right font-bold bg-emerald-900/20 dark:bg-emerald-200/20">
                {totals.receiptCount.toLocaleString()}
              </td>
              {/* ST-00182 — bottom line unchanged (BF/CF-adjusted), but ordered
                  weight / weight % / count so the first two line up with the
                  per-day matched columns above. */}
              {matchedAvailable ? (
                <>
                  {varianceNa ? (
                    <>
                      <td
                        className="px-3 sm:px-5 py-3.5 sm:py-4 whitespace-nowrap text-sm sm:text-base text-right font-bold border-l-2 border-l-slate-500/40 dark:border-l-slate-400/40 text-slate-300 dark:text-slate-600"
                        title={naHint || undefined}
                      >
                        n/a
                      </td>
                      <td
                        className="px-3 sm:px-5 py-3.5 sm:py-4 whitespace-nowrap text-sm sm:text-base text-right font-bold text-slate-300 dark:text-slate-600"
                        title={naHint || undefined}
                      >
                        n/a
                      </td>
                    </>
                  ) : (
                    <>
                      <td
                        className={`px-3 sm:px-5 py-3.5 sm:py-4 whitespace-nowrap text-sm sm:text-base text-right font-bold border-l-2 border-l-slate-500/40 dark:border-l-slate-400/40 ${weightVariance >= 0 ? 'text-emerald-300 dark:text-emerald-800' : 'text-red-300 dark:text-red-800'}`}
                      >
                        {weightVariance >= 0 ? '+' : ''}
                        {weightVariance.toLocaleString(undefined, {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        })}
                      </td>
                      <td
                        className={`px-3 sm:px-5 py-3.5 sm:py-4 whitespace-nowrap text-sm sm:text-base text-right font-bold ${weightVariancePercent >= 0 ? 'text-emerald-300 dark:text-emerald-800' : 'text-red-300 dark:text-red-800'}`}
                      >
                        {weightVariancePercent >= 0 ? '+' : ''}
                        {weightVariancePercent.toFixed(2)}%
                      </td>
                    </>
                  )}
                  <td
                    className={`px-3 sm:px-5 py-3.5 sm:py-4 whitespace-nowrap text-sm sm:text-base text-right font-bold ${countVariance >= 0 ? 'text-emerald-300 dark:text-emerald-800' : 'text-red-300 dark:text-red-800'}`}
                  >
                    <span className="mr-1 text-[9px] font-medium uppercase tracking-wider text-slate-400 dark:text-slate-500">
                      count
                    </span>
                    {countVariance >= 0 ? '+' : ''}
                    {countVariance.toLocaleString()}
                  </td>
                </>
              ) : (
                <td className="px-3 sm:px-5 py-3.5 sm:py-4 whitespace-nowrap text-right border-l-2 border-l-slate-500/40 dark:border-l-slate-400/40">
                  {varianceNa ? (
                    <div
                      className="text-sm sm:text-base font-bold text-slate-300 dark:text-slate-600"
                      title={naHint || undefined}
                    >
                      n/a
                    </div>
                  ) : (
                    <>
                      <div
                        className={`text-sm sm:text-base font-bold ${weightVariance >= 0 ? 'text-emerald-300 dark:text-emerald-800' : 'text-red-300 dark:text-red-800'}`}
                      >
                        {weightVariance >= 0 ? '+' : ''}
                        {weightVariance.toLocaleString(undefined, {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        })}
                      </div>
                      <div
                        className={`text-[11px] sm:text-xs font-bold ${weightVariancePercent >= 0 ? 'text-emerald-300 dark:text-emerald-800' : 'text-red-300 dark:text-red-800'}`}
                      >
                        {weightVariancePercent >= 0 ? '+' : ''}
                        {weightVariancePercent.toFixed(2)}%
                      </div>
                    </>
                  )}
                  <div
                    className={`text-[11px] sm:text-xs font-bold ${countVariance >= 0 ? 'text-emerald-300 dark:text-emerald-800' : 'text-red-300 dark:text-red-800'}`}
                  >
                    <span className="mr-1 text-[9px] font-medium uppercase tracking-wider text-slate-400 dark:text-slate-500">
                      count
                    </span>
                    {countVariance >= 0 ? '+' : ''}
                    {countVariance.toLocaleString()}
                  </div>
                </td>
              )}
            </tr>
          </tfoot>
        </table>
      </div>

      {bfcfPreview}

      {/* Summary Stats - Responsive design */}
      <div className="px-4 sm:px-6 py-3 sm:py-4 bg-slate-50/80 border-t border-slate-200">
        {/* Totals Grid */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-2 sm:gap-3 mb-3 sm:mb-4">
          <div className="text-center p-2.5 sm:p-3 bg-surface border border-orange-200 rounded-lg shadow-sm">
            <p className="text-[9px] sm:text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-0.5 sm:mb-1">
              Dispatch Weight
            </p>
            <p className="text-sm sm:text-base font-bold text-orange-600">
              {totals.dispatchWeight.toLocaleString(undefined, {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              })}
            </p>
            <p className="text-[9px] sm:text-[10px] text-slate-400 mt-0.5">tonnes</p>
          </div>
          <div className="text-center p-2.5 sm:p-3 bg-surface border border-orange-200 rounded-lg shadow-sm">
            <p className="text-[9px] sm:text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-0.5 sm:mb-1">
              Dispatch Count
            </p>
            <p className="text-sm sm:text-base font-bold text-orange-600">
              {totals.dispatchCount.toLocaleString()}
            </p>
            <p className="text-[9px] sm:text-[10px] text-slate-400 mt-0.5">loads</p>
          </div>
          <div className="text-center p-2.5 sm:p-3 bg-surface border border-emerald-200 rounded-lg shadow-sm">
            <p className="text-[9px] sm:text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-0.5 sm:mb-1">
              Receipt Weight
            </p>
            <p className="text-sm sm:text-base font-bold text-emerald-600">
              {totals.receiptWeight.toLocaleString(undefined, {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              })}
            </p>
            <p className="text-[9px] sm:text-[10px] text-slate-400 mt-0.5">tonnes</p>
          </div>
          <div className="text-center p-2.5 sm:p-3 bg-surface border border-emerald-200 rounded-lg shadow-sm">
            <p className="text-[9px] sm:text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-0.5 sm:mb-1">
              Receipt Count
            </p>
            <p className="text-sm sm:text-base font-bold text-emerald-600">
              {totals.receiptCount.toLocaleString()}
            </p>
            <p className="text-[9px] sm:text-[10px] text-slate-400 mt-0.5">loads</p>
          </div>
        </div>

        {/* Variance Analysis */}
        <div className="border-t border-slate-200 pt-3 sm:pt-4">
          <h3 className="text-[10px] sm:text-xs font-bold text-slate-900 mb-2 sm:mb-3 uppercase tracking-wider">
            Variance Analysis
          </h3>
          {naHint && (
            <p className="text-[11px] sm:text-xs text-slate-600 mb-2 sm:mb-3">
              Variance n/a — {naHint}
            </p>
          )}
          <div className="grid grid-cols-3 gap-2 sm:gap-3">
            <div
              className={`text-center p-2 sm:p-3 rounded-lg border-2 ${countVariance >= 0 ? 'bg-emerald-50 border-emerald-200' : 'bg-red-50 border-red-200'}`}
            >
              <p className="text-[9px] sm:text-[10px] font-bold text-slate-600 uppercase tracking-wider mb-0.5 sm:mb-1">
                Count Var.
              </p>
              <p
                className={`text-sm sm:text-lg font-bold ${countVariance >= 0 ? 'text-emerald-700' : 'text-red-700'}`}
              >
                {countVariance >= 0 ? '+' : ''}
                {countVariance.toLocaleString()}
              </p>
              <p className="text-[9px] sm:text-[10px] text-slate-600 mt-0.5">loads</p>
            </div>
            <div
              className={`text-center p-2 sm:p-3 rounded-lg border-2 ${varianceNa ? 'bg-slate-50 border-slate-200' : weightVariance >= 0 ? 'bg-emerald-50 border-emerald-200' : 'bg-red-50 border-red-200'}`}
            >
              <p className="text-[9px] sm:text-[10px] font-bold text-slate-600 uppercase tracking-wider mb-0.5 sm:mb-1">
                Weight Var.
              </p>
              <p
                className={`text-sm sm:text-lg font-bold ${varianceNa ? 'text-slate-500' : weightVariance >= 0 ? 'text-emerald-700' : 'text-red-700'}`}
              >
                {varianceNa ? (
                  'n/a'
                ) : (
                  <>
                    {weightVariance >= 0 ? '+' : ''}
                    {weightVariance.toLocaleString(undefined, {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    })}
                  </>
                )}
              </p>
              <p className="text-[9px] sm:text-[10px] text-slate-600 mt-0.5">tonnes</p>
            </div>
            <div
              className={`text-center p-2 sm:p-3 rounded-lg border-2 ${varianceNa ? 'bg-slate-50 border-slate-200' : weightVariancePercent >= 0 ? 'bg-emerald-50 border-emerald-200' : 'bg-red-50 border-red-200'}`}
            >
              <p className="text-[9px] sm:text-[10px] font-bold text-slate-600 uppercase tracking-wider mb-0.5 sm:mb-1">
                Weight Var. %
              </p>
              <p
                className={`text-sm sm:text-lg font-bold ${varianceNa ? 'text-slate-500' : weightVariancePercent >= 0 ? 'text-emerald-700' : 'text-red-700'}`}
              >
                {varianceNa ? (
                  'n/a'
                ) : (
                  <>
                    {weightVariancePercent >= 0 ? '+' : ''}
                    {weightVariancePercent.toFixed(2)}%
                  </>
                )}
              </p>
              <p className="text-[9px] sm:text-[10px] text-slate-600 mt-0.5">of dispatch</p>
            </div>
          </div>
        </div>
      </div>

      {/* Transaction Details Modal */}
      <TransactionDetailsModal
        isOpen={modalOpen}
        onClose={handleModalClose}
        date={selectedDate}
        type={selectedType}
        groupingId={groupingIds[0] || groupingId}
        groupingIds={groupingIds}
        minWeight={minWeight}
        groupingName={grouping.name || 'Route Summary'}
        bfcf={
          bfcfVariant
            ? {
                variant: bfcfVariant,
                groupingIds,
                startDate,
                endDate,
                minWeight,
                // ST-00218 P3: the list follows the matching this summary shows
                matching: matching?.effective ?? null,
              }
            : null
        }
      />

      {/* SQL Query Modal */}
      <SQLQueryModal
        isOpen={sqlModalOpen}
        onClose={handleSqlModalClose}
        query={sqlQuery}
        metadata={sqlMetadata}
        loading={sqlLoading}
        error={sqlError}
      />

      {/* Fixed-position multi-group sum calculator */}
      {hasAnySelection && (
        <div
          className="fixed bottom-5 left-1/2 z-50"
          style={{ transform: 'translateX(-50%)', animation: 'weightCalcFadeIn 200ms ease-out' }}
        >
          <style>{`
            @keyframes weightCalcFadeIn {
              from { opacity: 0; transform: translateX(-50%) translateY(12px); }
              to { opacity: 1; transform: translateX(-50%) translateY(0); }
            }
          `}</style>
          <div className="inline-flex items-center gap-2 bg-slate-900 dark:bg-slate-50 text-white px-3 py-2.5 rounded-xl shadow-2xl shadow-slate-900/40 dark:shadow-black/40 border border-slate-700 dark:border-slate-200">
            <FaCalculator className="w-3 h-3 text-slate-500 flex-shrink-0" />

            {selectionGroups.map((group, i) => {
              const color = GROUP_COLORS[i]!;
              const sum = getGroupSum(group);
              return (
                <Fragment key={i}>
                  {i > 0 && (
                    <span className="text-slate-600 dark:text-slate-400 text-xs px-0.5">vs</span>
                  )}
                  <div className="flex items-center gap-1.5 bg-slate-800 dark:bg-slate-50 pl-2.5 pr-1 py-1 rounded-lg">
                    <div className={`w-2.5 h-2.5 rounded-sm flex-shrink-0 ${color.dot}`} />
                    <span className="text-slate-400 dark:text-slate-600 text-[10px] font-medium">
                      {group.cells.size}
                    </span>
                    <span className="text-slate-600 dark:text-slate-400">:</span>
                    <span className="text-sm font-bold text-white tabular-nums">
                      {sum.toLocaleString(undefined, {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })}
                    </span>
                    <button
                      onClick={() => clearGroup(i)}
                      className="p-1 hover:bg-slate-700 dark:hover:bg-slate-200 rounded transition-colors text-slate-500 hover:text-white"
                      title={`Clear group ${color.label}`}
                    >
                      <FaTimes className="w-2.5 h-2.5" />
                    </button>
                  </div>
                </Fragment>
              );
            })}

            {selectionGroups.length > 1 && (
              <button
                onClick={clearAllGroups}
                className="ml-1 px-2 py-1 text-[10px] font-medium text-slate-400 dark:text-slate-600 hover:text-white hover:bg-slate-700 dark:hover:bg-slate-200 rounded-lg transition-colors"
                title="Clear all"
              >
                Clear all
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default SummaryTable;
